import React, { useState } from 'react';
import { OfficerLayout } from '../../components/layout/OfficerLayout';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { FilterChip } from '../../components/ui/FilterChip';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Download, FileText, PlayCircle, Image as ImageIcon, Search, Heart, Share2, Check, Clock, CloudOff } from 'lucide-react';
import { cn } from '../../lib/utils';
interface Material {
  id: string;
  title: string;
  type: 'pdf' | 'video' | 'poster' | 'ppt';
  size: string;
  downloaded: boolean;
  favorite: boolean;
  description: string;
  date: string;
  category: string;
}
const MOCK_MATERIALS: Material[] = [{
  id: '1',
  title: 'SOP Bank Sampah Unit 2024',
  type: 'pdf',
  size: '2.4 MB',
  downloaded: true,
  favorite: true,
  description: 'Panduan lengkap operasional bank sampah unit sesuai regulasi terbaru.',
  date: '10 Jan 2024',
  category: 'SOP'
}, {
  id: '2',
  title: 'Tutorial Maggot BSF',
  type: 'video',
  size: '45 MB',
  downloaded: false,
  favorite: false,
  description: 'Video tutorial budidaya maggot BSF untuk pengolahan sampah organik.',
  date: '15 Jan 2024',
  category: 'Video'
}, {
  id: '3',
  title: 'Jadwal Angkut Sampah',
  type: 'poster',
  size: '1.2 MB',
  downloaded: true,
  favorite: false,
  description: 'Poster jadwal pengangkutan sampah terpilah untuk ditempel di RT/RW.',
  date: '20 Jan 2024',
  category: 'Poster'
}, {
  id: '4',
  title: 'Materi Sosialisasi RT/RW',
  type: 'ppt',
  size: '5.8 MB',
  downloaded: false,
  favorite: true,
  description: 'Slide presentasi untuk sosialisasi pemilahan sampah ke warga.',
  date: '05 Feb 2024',
  category: 'Presentasi'
}];
export function MateriPage() {
  const [activeTab, setActiveTab] = useState<'all' | 'favorite' | 'downloaded'>('all');
  const [activeFilter, setActiveFilter] = useState('Semua');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [materials, setMaterials] = useState(MOCK_MATERIALS);
  const toggleFavorite = (id: string) => {
    setMaterials(materials.map(m => m.id === id ? {
      ...m,
      favorite: !m.favorite
    } : m));
  };
  const toggleDownload = (id: string) => {
    // Simulate download
    setMaterials(materials.map(m => m.id === id ? {
      ...m,
      downloaded: !m.downloaded
    } : m));
  };
  const filteredMaterials = materials.filter(m => {
    const matchesSearch = m.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = activeFilter === 'Semua' || activeFilter === 'Poster' && m.type === 'poster' || activeFilter === 'Video' && m.type === 'video' || activeFilter === 'SOP' && m.type === 'pdf' || activeFilter === 'Presentasi' && m.type === 'ppt';
    const matchesTab = activeTab === 'all' || activeTab === 'favorite' && m.favorite || activeTab === 'downloaded' && m.downloaded;
    return matchesSearch && matchesFilter && matchesTab;
  });
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <FileText className="h-10 w-10" data-id="element-3394" />;
      case 'video':
        return <PlayCircle className="h-10 w-10" data-id="element-3395" />;
      case 'poster':
        return <ImageIcon className="h-10 w-10" data-id="element-3396" />;
      case 'ppt':
        return <FileText className="h-10 w-10" data-id="element-3397" />;
      default:
        return <FileText className="h-10 w-10" data-id="element-3398" />;
    }
  };
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'pdf':
        return 'bg-red-500';
      case 'video':
        return 'bg-blue-500';
      case 'poster':
        return 'bg-green-500';
      case 'ppt':
        return 'bg-orange-500';
      default:
        return 'bg-gray-500';
    }
  };
  return <OfficerLayout title="Materi & Toolkit" data-id="element-3399">
      {/* Search & Tabs */}
      <div className="space-y-4 mb-6" data-id="element-3400">
        <div className="relative" data-id="element-3401">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-3402" />
          <Input placeholder="Cari materi..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3403" />
        </div>

        <div className="flex p-1 bg-gray-100 rounded-lg" data-id="element-3404">
          {['all', 'favorite', 'downloaded'].map(tab => <button key={tab} className={cn('flex-1 py-1.5 text-xs font-medium rounded-md transition-all', activeTab === tab ? 'bg-white shadow text-primary-600' : 'text-gray-500')} onClick={() => setActiveTab(tab as any)} data-id="element-3405">
              {tab === 'all' ? 'Semua' : tab === 'favorite' ? 'Favorit' : 'Terunduh'}
            </button>)}
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar" data-id="element-3406">
          {['Semua', 'Poster', 'Video', 'SOP', 'Presentasi'].map(filter => <FilterChip key={filter} label={filter} active={activeFilter === filter} onClick={() => setActiveFilter(filter)} data-id="element-3407" />)}
        </div>
      </div>

      {/* Recently Viewed (Horizontal Scroll) */}
      {activeTab === 'all' && !searchTerm && <div className="mb-6" data-id="element-3408">
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-3409">
            <Clock className="h-4 w-4 text-gray-500" data-id="element-3410" /> Terakhir Dilihat
          </h3>
          <div className="flex gap-3 overflow-x-auto pb-4 no-scrollbar" data-id="element-3411">
            {materials.slice(0, 3).map(item => <div key={item.id} className="w-32 flex-shrink-0 cursor-pointer" onClick={() => setSelectedMaterial(item)} data-id="element-3412">
                <div className="aspect-[3/4] bg-gray-200 rounded-lg relative overflow-hidden mb-2" data-id="element-3413">
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400 bg-gray-100" data-id="element-3414">
                    {getTypeIcon(item.type)}
                  </div>
                  {item.downloaded && <div className="absolute bottom-1 right-1 bg-green-500 rounded-full p-0.5 border-2 border-white" data-id="element-3415">
                      <Check className="h-2 w-2 text-white" data-id="element-3416" />
                    </div>}
                </div>
                <p className="text-xs font-medium text-gray-900 line-clamp-2" data-id="element-3417">
                  {item.title}
                </p>
              </div>)}
          </div>
        </div>}

      {/* Grid */}
      <div className="grid grid-cols-2 gap-3 pb-4" data-id="element-3418">
        {filteredMaterials.map(item => <Card key={item.id} className="overflow-hidden group active:scale-95 transition-transform cursor-pointer" onClick={() => setSelectedMaterial(item)} data-id="element-3419">
            <div className="aspect-[3/4] bg-gray-200 relative" data-id="element-3420">
              <div className="absolute top-2 left-2" data-id="element-3421">
                <Badge className={`${getTypeColor(item.type)} text-white text-[10px] uppercase`} data-id="element-3422">
                  {item.type}
                </Badge>
              </div>
              {item.favorite && <div className="absolute top-2 right-2" data-id="element-3423">
                  <Heart className="h-4 w-4 text-red-500 fill-red-500" data-id="element-3424" />
                </div>}
              <div className="absolute inset-0 flex items-center justify-center text-gray-400 bg-gray-100" data-id="element-3425">
                {getTypeIcon(item.type)}
              </div>
              {item.downloaded && <div className="absolute bottom-0 left-0 right-0 bg-green-500/90 text-white text-[10px] py-1 text-center font-medium" data-id="element-3426">
                  Tersedia Offline
                </div>}
            </div>
            <CardContent className="p-3" data-id="element-3427">
              <h4 className="text-xs font-bold text-gray-900 line-clamp-2 mb-2 h-8" data-id="element-3428">
                {item.title}
              </h4>
              <div className="flex justify-between items-center text-[10px] text-gray-500" data-id="element-3429">
                <span data-id="element-3430">{item.size}</span>
                <span data-id="element-3431">{item.date}</span>
              </div>
            </CardContent>
          </Card>)}
      </div>

      {/* Material Detail Modal */}
      <Modal isOpen={!!selectedMaterial} onClose={() => setSelectedMaterial(null)} title="Detail Materi" size="sm" className="mb-0 rounded-b-none sm:rounded-b-lg sm:mb-auto fixed bottom-0 sm:relative w-full sm:w-auto" footer={<div className="w-full flex gap-2" data-id="element-3433">
            <Button variant="outline" className="flex-1" onClick={() => {
        if (selectedMaterial) toggleFavorite(selectedMaterial.id);
      }} data-id="element-3434">
              <Heart className={`h-4 w-4 mr-2 ${selectedMaterial?.favorite ? 'fill-red-500 text-red-500' : ''}`} data-id="element-3435" />
              {selectedMaterial?.favorite ? 'Favorit' : 'Simpan'}
            </Button>
            <Button className={`flex-1 ${selectedMaterial?.downloaded ? 'bg-green-600' : 'bg-primary-600'}`} onClick={() => {
        if (selectedMaterial) toggleDownload(selectedMaterial.id);
      }} data-id="element-3436">
              {selectedMaterial?.downloaded ? <>
                  <Check className="h-4 w-4 mr-2" data-id="element-3437" /> Buka
                </> : <>
                  <Download className="h-4 w-4 mr-2" data-id="element-3438" /> Unduh
                </>}
            </Button>
          </div>} data-id="element-3432">
        {selectedMaterial && <div className="space-y-4" data-id="element-3439">
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center border relative" data-id="element-3440">
              {getTypeIcon(selectedMaterial.type)}
              <div className="absolute top-2 right-2" data-id="element-3441">
                <Button size="icon" variant="ghost" className="h-8 w-8 bg-white/80 rounded-full" data-id="element-3442">
                  <Share2 className="h-4 w-4 text-gray-600" data-id="element-3443" />
                </Button>
              </div>
            </div>

            <div data-id="element-3444">
              <div className="flex items-center gap-2 mb-1" data-id="element-3445">
                <Badge className={`${getTypeColor(selectedMaterial.type)} text-white text-[10px] uppercase`} data-id="element-3446">
                  {selectedMaterial.type}
                </Badge>
                <span className="text-xs text-gray-500" data-id="element-3447">
                  {selectedMaterial.size}
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-3448">
                {selectedMaterial.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3449">
                {selectedMaterial.description}
              </p>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg border" data-id="element-3450">
              <h4 className="text-xs font-bold text-gray-900 mb-2" data-id="element-3451">
                Informasi File
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-3452">
                <div data-id="element-3453">
                  <span className="text-gray-500 block" data-id="element-3454">Kategori</span>
                  <span className="font-medium" data-id="element-3455">
                    {selectedMaterial.category}
                  </span>
                </div>
                <div data-id="element-3456">
                  <span className="text-gray-500 block" data-id="element-3457">Tanggal Upload</span>
                  <span className="font-medium" data-id="element-3458">{selectedMaterial.date}</span>
                </div>
                <div data-id="element-3459">
                  <span className="text-gray-500 block" data-id="element-3460">Status Offline</span>
                  <span className={`font-medium flex items-center gap-1 ${selectedMaterial.downloaded ? 'text-green-600' : 'text-gray-400'}`} data-id="element-3461">
                    {selectedMaterial.downloaded ? <Check className="h-3 w-3" data-id="element-3462" /> : <CloudOff className="h-3 w-3" data-id="element-3463" />}
                    {selectedMaterial.downloaded ? 'Tersedia' : 'Belum Diunduh'}
                  </span>
                </div>
              </div>
            </div>
          </div>}
      </Modal>
    </OfficerLayout>;
}